/**
 * Contact-form behaviour: no DOM, no network — both arrive injected, so every
 * path here is testable with plain objects.
 */

import { ApiClient, ApiError } from "./api-client.js";
import { CONTACT_FIELDS, type ContactField } from "./contract.js";
import type { ContactView } from "./contact-view.js";

export type ContactControllerDependencies = {
  readonly view: ContactView;
  readonly api: Pick<ApiClient, "submitContact">;
};

/** Only fields the form has an input for; anything else goes in the summary. */
function fieldErrorsFrom(
  details: Readonly<Record<string, string>>,
): Partial<Record<ContactField, string>> {
  const result: Partial<Record<ContactField, string>> = {};

  for (const field of CONTACT_FIELDS) {
    const message = details[field];
    if (message !== undefined) result[field] = message;
  }

  return result;
}

export class ContactController {
  private inFlight = false;

  constructor(private readonly deps: ContactControllerDependencies) {}

  /** Attaches to the view. Call once. */
  start(): void {
    this.deps.view.render({ kind: "idle" });
    this.deps.view.onSubmit(() => {
      void this.submit();
    });
  }

  async submit(): Promise<void> {
    // Guards against double submits from repeated clicks or Enter presses.
    if (this.inFlight) return;

    const values = this.deps.view.getValues();
    const payload = {
      name: values.name.trim(),
      company: values.company.trim(),
      email: values.email.trim(),
      note: values.note.trim(),
    };

    // Cheap client-side check for the two required fields, so an obvious
    // omission does not need a round trip. The server remains the authority.
    const missing = fieldErrorsFrom({
      ...(payload.name === "" ? { name: "Please enter your name." } : {}),
      ...(payload.email === "" ? { email: "Please enter your email." } : {}),
    });

    if (Object.keys(missing).length > 0) {
      this.deps.view.render({
        kind: "error",
        message: "Please fill in the required fields.",
        fieldErrors: missing,
      });
      return;
    }

    this.inFlight = true;
    this.deps.view.render({ kind: "submitting" });

    try {
      await this.deps.api.submitContact(payload);
      this.deps.view.clear();
      this.deps.view.render({ kind: "sent" });
    } catch (error) {
      this.deps.view.render({
        kind: "error",
        message: describeFailure(error),
        fieldErrors:
          error instanceof ApiError ? fieldErrorsFrom(error.details) : {},
      });
    } finally {
      this.inFlight = false;
    }
  }
}

function describeFailure(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return "Could not reach the server. Please check your connection.";
}
